/**
 * Thin React wrapper around `react-plaid-link`'s `usePlaidLink` hook.
 *
 * Why a wrapper instead of using the hook directly in IntegrationsPage?
 *   - The hook *requires* a non-null link_token at mount time, but Atlas
 *     mints the token on demand (so users don't pay Plaid Link costs for
 *     pages they never click "Connect" on). Encapsulating the
 *     mint-then-open dance here keeps IntegrationsPage simple.
 *   - The success callback hands us a public_token + institution metadata,
 *     which we forward straight to plaid-exchange-token. Doing this in one
 *     place means there's a single audit trail / single error toast path.
 *
 * OAuth-return handling
 * ---------------------
 * Most major US banks (Chase, Capital One, Wells Fargo, Bank of America,
 * etc.) use full-redirect OAuth in production: the user is bounced from
 * Plaid Link out to the bank's domain, authenticates, then the bank
 * redirects to Plaid, which redirects to the URI we registered in the
 * Plaid dashboard (Atlas: `${origin}/integrations`). On that return, we
 * re-instantiate `usePlaidLink` with the SAME link_token we used to start
 * the flow plus `receivedRedirectUri: window.location.href`, and Plaid
 * Link reopens itself to complete the public_token exchange.
 *
 * AMEX uses Plaid's embedded OAuth (auth happens inside the Link iframe),
 * so AMEX-only flows would actually work without any of this. We support
 * the redirect dance anyway so that adding Chase / Discover / Fifth Third
 * later is zero-code.
 *
 * sessionStorage is used (not localStorage) deliberately — the OAuth
 * round-trip stays in the same browser tab, so the state is naturally
 * scoped, and an abandoned flow self-cleans on tab close.
 */
import { useCallback, useEffect, useState } from 'react';
import { usePlaidLink, type PlaidLinkOnSuccessMetadata } from 'react-plaid-link';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';

interface MintResponse {
  ok: boolean;
  link_token?: string;
  expiration?: string;
  env?: string;
  error?: string;
}

interface ExchangeResponse {
  ok: boolean;
  plaid_item_id?: string;
  institution_name?: string | null;
  error?: string;
}

export interface PlaidLinkController {
  /** True while a Link token is being minted or Link is open. */
  loading: boolean;
  /** Open the Plaid Link modal. Mints a fresh token on each click. */
  open: () => void | Promise<void>;
  /** True once the hook is ready to render Link. Internal — usually you can ignore. */
  ready: boolean;
}

/**
 * Stashed across the OAuth bounce so the hook can resume after the bank
 * redirects back. We persist the full link_token (not just a flag) because
 * Plaid requires the *same* token on resume — minting a fresh one would
 * orphan the OAuth session Plaid is holding open server-side.
 */
interface OauthState {
  link_token: string;
  /**
   * `undefined` for a fresh "Connect a bank" flow; set to the
   * `plaid_item_id` for an "update mode" reconnect of an existing item.
   * On OAuth return, only the `usePlaidConnector` instance whose
   * `updateItemId` matches this field claims the token.
   */
  update_item_id?: string;
}

const OAUTH_SESSION_KEY = 'atlas.plaidOauthState';

function getOauthState(): OauthState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(OAUTH_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OauthState;
    if (typeof parsed?.link_token !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

function setOauthState(state: OauthState | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (state) {
      window.sessionStorage.setItem(OAUTH_SESSION_KEY, JSON.stringify(state));
    } else {
      window.sessionStorage.removeItem(OAUTH_SESSION_KEY);
    }
  } catch {
    // sessionStorage can be disabled (privacy mode, embedded contexts).
    // OAuth flows will simply fail to resume, which surfaces as a regular
    // "Plaid Link expired" error toast — acceptable degradation.
  }
}

/**
 * Plaid appends `?oauth_state_id=<state>` to the redirect_uri when bouncing
 * the user back from the bank's OAuth flow. Detecting this marker is the
 * canonical way to know we're resuming a flow vs. starting a fresh one.
 * (See Plaid's "OAuth integration" guide.)
 */
function isOauthReturn(): boolean {
  if (typeof window === 'undefined') return false;
  return /[?&]oauth_state_id=/.test(window.location.search);
}

/**
 * Compute the redirect URI we'll register with Plaid. Always anchored on
 * the current origin + `/integrations` so dev (`localhost:5173`) and prod
 * (`atlas.virrolife.com`) work without server-side branching.
 *
 * The string we return MUST be byte-identical to one of the URIs the
 * operator registered in Plaid Dashboard → Team Settings → API →
 * Allowed redirect URIs, or `/link/token/create` will 400.
 */
function plaidRedirectUri(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return `${window.location.origin}/integrations`;
}

/**
 * Resolve OAuth-return state for this hook instance. Returns null if:
 *   - the URL has no oauth_state_id (we're starting a fresh flow), OR
 *   - sessionStorage is empty (probably a stale tab the user reopened),
 *     OR
 *   - the saved state's mode (connect vs. update-<item>) doesn't match
 *     this hook's `updateItemId` — i.e. another sibling instance owns it.
 */
function resolveOauthReturn(
  updateItemId: string | undefined,
): { linkToken: string; href: string } | null {
  if (typeof window === 'undefined') return null;
  if (!isOauthReturn()) return null;
  const saved = getOauthState();
  if (!saved) return null;
  if ((updateItemId ?? null) !== (saved.update_item_id ?? null)) return null;
  return { linkToken: saved.link_token, href: window.location.href };
}

/**
 * onLinked: called after plaid-exchange-token returns successfully so the
 *   caller can refresh queries (integrations, plaid_items, bank_accounts).
 *
 * updateItemId: pass to re-authenticate an existing Plaid Item (Update mode).
 *   Plaid's Update flow doesn't return a new public_token; it just refreshes
 *   the existing item's credentials. We still call the success path so the
 *   UI invalidates and any "login_required" status flips back.
 */
export function usePlaidConnector(opts: {
  onLinked?: (info: { plaidItemId: string; institutionName: string | null }) => void;
  updateItemId?: string;
}): PlaidLinkController {
  // On OAuth return, restore the link_token we stashed before the bank
  // redirect — the same token MUST be reused or Plaid will 400. Lazy
  // initializer so we read sessionStorage exactly once on mount.
  const [linkToken, setLinkToken] = useState<string | null>(() => {
    return resolveOauthReturn(opts.updateItemId)?.linkToken ?? null;
  });
  const [loading, setLoading] = useState(false);
  // Captured at mount: the URL Plaid redirected us back to. Cleared after
  // onSuccess/onExit so a follow-up "Connect another" click in the same
  // SPA session doesn't accidentally pass a stale receivedRedirectUri.
  const [receivedRedirectUri, setReceivedRedirectUri] = useState<string | undefined>(
    () => resolveOauthReturn(opts.updateItemId)?.href,
  );

  /**
   * Wipe both the sessionStorage breadcrumb and the `?oauth_state_id`
   * URL marker. Without the URL strip, a refresh after a successful
   * connect would re-trigger OAuth detection with a now-expired token
   * and surface a confusing error.
   */
  const cleanupOauthArtifacts = useCallback(() => {
    setOauthState(null);
    setReceivedRedirectUri(undefined);
    if (typeof window !== 'undefined' && isOauthReturn()) {
      // Strip every Plaid-injected query param but preserve any others
      // the host page cares about (e.g. ?tab=plaid filters).
      const url = new URL(window.location.href);
      url.searchParams.delete('oauth_state_id');
      window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    }
  }, []);

  const onSuccess = useCallback(
    async (publicToken: string, metadata: PlaidLinkOnSuccessMetadata) => {
      try {
        const { data, error } = await supabase.functions.invoke<ExchangeResponse>(
          'plaid-exchange-token',
          {
            body: {
              public_token: publicToken,
              institution_id: metadata.institution?.institution_id ?? null,
              institution_name: metadata.institution?.name ?? null,
            },
          },
        );
        if (error || !data?.ok) {
          const msg = data?.error ?? error?.message ?? 'Exchange failed';
          toast.error(`Plaid: ${msg}`);
          return;
        }
        toast.success(
          `${data.institution_name ?? 'Bank'} connected. Pulling accounts and transactions…`,
        );
        opts.onLinked?.({
          plaidItemId: data.plaid_item_id ?? '',
          institutionName: data.institution_name ?? null,
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Exchange failed';
        toast.error(`Plaid: ${msg}`);
      } finally {
        // Force a fresh mint on the next click — link_tokens are
        // single-flow and Plaid expires them after ~30 minutes anyway.
        setLinkToken(null);
        setLoading(false);
        cleanupOauthArtifacts();
      }
    },
    [opts, cleanupOauthArtifacts],
  );

  const onExit = useCallback(() => {
    // The user dismissed Link. Clear the token so the next "Connect" press
    // re-mints rather than re-using a stale one.
    setLinkToken(null);
    setLoading(false);
    cleanupOauthArtifacts();
  }, [cleanupOauthArtifacts]);

  // The hook *insists* on a stable token, so we feed it whatever's currently
  // in state. When linkToken === null, ready is false and open() does nothing.
  // `receivedRedirectUri` is undefined for fresh flows and a real URL only
  // when we're resuming after the bank's OAuth bounce.
  const { open: plaidOpen, ready } = usePlaidLink({
    token: linkToken,
    onSuccess,
    onExit,
    receivedRedirectUri,
  });

  // Auto-open as soon as the hook reports ready (i.e. the moment Plaid's
  // script has consumed the token). Without this auto-trigger the user
  // would have to click twice — once to mint, once to open. On OAuth return
  // this also drives the seamless "page loads → Link reopens" experience.
  useEffect(() => {
    if (ready && linkToken) {
      plaidOpen();
    }
  }, [ready, linkToken, plaidOpen]);

  const open = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    try {
      const redirectUri = plaidRedirectUri();
      const { data, error } = await supabase.functions.invoke<MintResponse>(
        'plaid-link-token',
        {
          body: {
            ...(opts.updateItemId ? { update_item_id: opts.updateItemId } : {}),
            ...(redirectUri ? { redirect_uri: redirectUri } : {}),
          },
        },
      );
      if (error || !data?.ok || !data.link_token) {
        const msg = data?.error ?? error?.message ?? 'Could not start Plaid Link';
        toast.error(msg);
        setLoading(false);
        return;
      }
      // Persist BEFORE Plaid Link redirects out of the SPA. For OAuth
      // institutions the bank's auth flow will navigate the entire window
      // away from /integrations, so any in-memory React state is lost —
      // sessionStorage is the only durable place to stash the token until
      // we land back on /integrations with `?oauth_state_id=…`.
      setOauthState({
        link_token: data.link_token,
        update_item_id: opts.updateItemId,
      });
      setLinkToken(data.link_token);
      // Note: plaidOpen() is fired by the useEffect above once `ready`
      // flips true. We don't call it here because `ready` may still be
      // stale from a previous render.
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Could not start Plaid Link';
      toast.error(msg);
      setLoading(false);
    }
  }, [loading, opts.updateItemId]);

  return { loading, open, ready };
}
